import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import cloneDeep from 'lodash.clonedeep';
import unset from 'lodash.unset';

import { getSectionQueryBySection } from '../context/utils/getSectionQuery';
import type { SectionQuery } from '../context/types/store';
import type { BaseSection, Section } from '../sections/types';
import { normalizeSection, normalizeSections } from '../context/utils/normalizeSections';
import { flattenArray } from '../context/utils/flattenArray';
import {
  isDebounceCancelError,
  useDebouncedCallbackWithPromise,
} from '../context/utils/useDebouncedCallbackWithPromise';
import { getDependenciesValues } from '../context/utils/getDependencyValues';
import { ContestManageContext } from '../context/ContestManageContext';
import { parseFieldResponse } from '../context/parsers/parseFieldResponse';
import {
  createRemoveSectionCacheAction,
  createSetFieldAction,
  createSetSectionAction,
  createSetSectionCacheAction,
  createUpdateSectionAction,
  createUpdateSectionCacheAction,
} from '../context/store/store.actions';
import { getManageEndpointData } from '../../../server/service';

export const useSectionQuery = (sectionKey: string) => {
  const { values, setValues, setTouched, store, dispatchStoreAction, errors } =
    useContext(ContestManageContext);
  const abortControllerRef = useRef<Record<string, AbortController>>({});
  const [retryFn, setRetryFn] = useState<() => void>(null);
  const section = useMemo(() => store.sections[sectionKey], [sectionKey, store]);

  const sectionQuery = useMemo(() => {
    if (!section) {
      return;
    }

    return getSectionQueryBySection(store, section);
  }, [section, store]);

  const storeRef = useRef(store);

  storeRef.current = store;

  const queryFn = useCallback(
    ({
      newSectionQueryKey,
      dependencyValues,
      // eslint-disable-next-line @typescript-eslint/no-shadow
      section,
    }: {
      newSectionQueryKey: string;
      dependencyValues: Record<string, unknown>;
      section: SectionQuery;
    }) => {
      if (section.type !== 'DynamicSection' && section.type !== 'DynamicSections') {
        return;
      }

      const prevQueryAbortController = abortControllerRef.current[section.latestQueryKey];
      if (prevQueryAbortController) {
        // FIXME: Temporary fix for aborting requests so all other reducer actions can be dispatched before cancellation
        setTimeout(() => {
          prevQueryAbortController.abort();
        }, 1);
      }

      const newAbortController = new AbortController();
      abortControllerRef.current[newSectionQueryKey] = newAbortController;

      if (section.type === 'DynamicSection') {
        return getManageEndpointData<BaseSection>(
          section.endpointName,
          dependencyValues,
          section.endpointMethod,
          newAbortController,
          section.queryParams
        ).then((data) => {
          const fieldKeyPrefix = `${sectionKey}:${newSectionQueryKey}:`;
          const normalizedSectionData = normalizeSection(data, fieldKeyPrefix);

          if (normalizedSectionData.section.type !== 'BaseSection') {
            throw new Error('Section type is not supported');
          }

          normalizedSectionData.fields.forEach((f) => {
            const normalizedFields = parseFieldResponse(f, fieldKeyPrefix);
            if (!normalizedFields?.length) {
              return;
            }

            flattenArray(normalizedFields).forEach((normalizedField) => {
              dispatchStoreAction(createSetFieldAction(normalizedField.key, normalizedField));
            });
          });

          dispatchStoreAction(
            createUpdateSectionCacheAction(newSectionQueryKey, {
              key: normalizedSectionData.section.key,
              data: normalizedSectionData.section,
              isLoading: false,
              error: undefined,
            })
          );
        });
      }
      if (section.type === 'DynamicSections') {
        return getManageEndpointData<Section[]>(
          section.endpointName,
          dependencyValues,
          section.endpointMethod,
          newAbortController,
          section.queryParams
        ).then((data) => {
          const keyPrefix = `${sectionKey}:${newSectionQueryKey}:`;
          const normalizedSectionData = normalizeSections(data, keyPrefix);

          normalizedSectionData.fields.forEach((f) => {
            const normalizedFields = parseFieldResponse(f, keyPrefix);
            if (!normalizedFields?.length) {
              return;
            }

            flattenArray(normalizedFields).forEach((normalizedField) => {
              dispatchStoreAction(createSetFieldAction(normalizedField.key, normalizedField));
            });
          });

          normalizedSectionData.sections.forEach((s) => {
            const newSectionKey = `${keyPrefix}${s.key}`;

            let parsedSection: SectionQuery;
            switch (s.type) {
              case 'BaseSection': {
                parsedSection = {
                  key: s.key,
                  type: 'BaseSection',
                  data: s,
                };
                break;
              }
              case 'DynamicSection':
              case 'DynamicSections': {
                parsedSection = {
                  ...s,
                };
                break;
              }
              default: {
                // eslint-disable-next-line no-console
                console.log('Unknown section type', section);
              }
            }

            if (!parsedSection) {
              return;
            }
            dispatchStoreAction(createSetSectionAction(newSectionKey, parsedSection));
          });
          dispatchStoreAction(
            createUpdateSectionCacheAction(newSectionQueryKey, {
              data: normalizedSectionData.sections.map((s) => `${keyPrefix}${s.key}`),
              isLoading: false,
              error: undefined,
            })
          );
        });
      }
    },
    [dispatchStoreAction, sectionKey]
  );

  const memoizedQueryFn = useDebouncedCallbackWithPromise(queryFn, 500);

  useEffect(() => {
    if (section.type !== 'DynamicSection' && section.type !== 'DynamicSections') {
      return;
    }

    const dependencyValues = getDependenciesValues(values, sectionKey, section.dependsOn);
    const currentSectionQuery = getSectionQueryBySection(storeRef.current, section);
    const newSectionQueryKey = `${section.endpointName}:${JSON.stringify(dependencyValues)}`;

    if (
      section.latestQueryKey === newSectionQueryKey &&
      (currentSectionQuery.isLoading || currentSectionQuery?.data || currentSectionQuery?.error)
    ) {
      return;
    }

    dispatchStoreAction(
      createUpdateSectionAction(sectionKey, {
        latestQueryKey: newSectionQueryKey,
      })
    );

    const cleanupPreviousData = () => {
      if (section.type === 'DynamicSection') {
        if (currentSectionQuery.type !== 'DynamicSection' || !currentSectionQuery.data) {
          return;
        }

        setValues((prev) => {
          const newPrev = cloneDeep(prev);
          unset(newPrev, [sectionKey, currentSectionQuery.data.key]);
          return newPrev;
        });
        setTouched((prev) => {
          const newPrev = cloneDeep(prev);
          unset(newPrev, [sectionKey, currentSectionQuery.data.key]);
          return newPrev;
        });
      } else if (section.type === 'DynamicSections') {
        if (currentSectionQuery.type !== 'DynamicSections') {
          return;
        }

        currentSectionQuery.data?.forEach((sectionQueryKey) => {
          const childSection = storeRef.current.sections[sectionQueryKey];
          const childSectionQuery = getSectionQueryBySection(storeRef.current, childSection);
          if (
            !childSectionQuery ||
            !childSectionQuery.data ||
            childSectionQuery.type === 'DynamicSections'
          ) {
            return;
          }

          setValues((prev) => {
            const newPrev = cloneDeep(prev);
            unset(newPrev, childSectionQuery.data.key);
            return newPrev;
          });
          setTouched((prev) => {
            const newPrev = cloneDeep(prev);
            unset(newPrev, childSectionQuery.data.key);
            return newPrev;
          });
        });
      }
    };

    cleanupPreviousData();

    // If new query key is not in cache, then we need to fetch it
    if (!storeRef.current.sectionsCache[newSectionQueryKey]) {
      let retries = 0;
      const callQueryFn = () => {
        dispatchStoreAction(
          createSetSectionCacheAction(newSectionQueryKey, {
            key: sectionKey,
            type: section.type,
            isLoading: true,
          })
        );

        memoizedQueryFn({
          newSectionQueryKey,
          dependencyValues,
          section,
        }).catch((error) => {
          if (axios.isCancel(error) || isDebounceCancelError(error)) {
            dispatchStoreAction(createRemoveSectionCacheAction(newSectionQueryKey));
            return;
          }
          if (axios.isAxiosError(error) && error.code === 'ERR_NETWORK') {
            if (retries < 2) {
              retries += 1;

              return new Promise((resolve) => {
                setTimeout(() => {
                  resolve(callQueryFn());
                }, 1000);
              });
            }
            retries = 0;
          }
          dispatchStoreAction(
            createUpdateSectionCacheAction(newSectionQueryKey, {
              isLoading: false,
              error,
            })
          );

          setRetryFn(() => () => {
            callQueryFn();
          });
        });
      };
      callQueryFn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatchStoreAction,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(errors),
    section,
    sectionKey,
    setValues,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(values),
    memoizedQueryFn,
    setTouched,
  ]);

  return {
    sectionQuery,
    retryFn,
  };
};
